import React, {useMemo} from 'react';
import {assert} from 'assert-ts';
import {Concept} from 'types';
import type {PreviewConfiguration} from 'schema/types';
import type {Data} from 'schemaDefinition/types';
import {type ExpressionV4, type ManifestationV4} from 'services/metadata/types';
import {sortManifestations} from 'services/metadata';
import {useExpressionExports} from 'services/metadata/hooks';
import {Card, Layout, Spacer, Text} from 'components';
import {useEntitySchema} from 'schemas';
import {ReadonlyExpressionCard} from 'widgets/metadata/readonlyExpressionCard/ReadonlyExpressionCard';
import {useExpressionTitle} from 'scenes/updateMetadata/components/ExpressionCard/hooks/useExpressionTitle';
import {ManifestationPreview} from './ManifestationPreview';

type ExpressionPreviewProps = {
  expression: ExpressionV4;
  manifestations: ManifestationV4[];
  expandedManifestationId?: string;
  configuration?: Pick<
    PreviewConfiguration,
    'linking' | 'onNavigate' | 'layout' | 'relatedScope'
  >;
};
export const ExpressionPreview: React.FC<ExpressionPreviewProps> = ({
  expression,
  manifestations,
  expandedManifestationId,
  configuration,
}) => {
  const title = useExpressionTitle(expression);
  const schemaExpression = useEntitySchema(Concept.expression);
  const expressionExports = useExpressionExports(expression);
  const configurationWithRelatedScope = useMemo(
    () => ({
      ...assert(configuration, 'ExpressionPreview: configuration expected'),
      relatedScope: {
        ...configuration?.relatedScope,
        expression: expressionExports,
      } as Data,
    }),
    [configuration, expressionExports],
  );

  const sortedManifestations = useMemo(() => {
    return sortManifestations(manifestations) ?? [];
  }, [manifestations]);

  return (
    <Card color="primary" mode="light" headerSpacing={0}>
      <Card.Header>
        <Layout horizontal flex={1} alignItems="center">
          <Text variant="h4">{title}</Text>
        </Layout>
      </Card.Header>
      <Card.Content>
        <ReadonlyExpressionCard
          expression={expression}
          schema={schemaExpression}
          configuration={configurationWithRelatedScope}
        />
        <Spacer size={2} />
        {sortedManifestations.map(m => (
          <ManifestationPreview
            key={m.id}
            manifestation={m}
            isExpanded={m.id === expandedManifestationId}
            configuration={configurationWithRelatedScope}
          />
        ))}
      </Card.Content>
    </Card>
  );
};
